<!-- 播放器 -->
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import SongActionsMenu from '~/components/SongMoreActionsPopover.vue'
import type { GeneratePrefillPayload } from '~/types/generate'
import { useToggleUpvote } from '~/composables/useCommonFunction'
import { setIsNotNew } from '~/apis/app'

// SVG Icons
import PreIcon from '~/assets/icons/player/pre.svg?component'
import NextIcon from '~/assets/icons/player/nex.svg?component'
import ListIcon from '~/assets/icons/player/list.svg?component'
import LikeIcon from '~/assets/icons/player/like.svg?component'
import LikeActiveIcon from '~/assets/icons/player/like_active.svg?component'
import ShareIcon from '~/assets/icons/player/share.svg?component'
import RemakeIcon from '~/assets/icons/player/remake.svg?component'
import VolumeIcon from '~/assets/icons/player/volume.svg?component'
import OrderOneByOneIcon from '~/assets/icons/player/order_one_by_one.svg?component'
import OrderSingleLoopIcon from '~/assets/icons/player/order_single_loop.svg?component'
import Loading from '~/assets/icons/loading.svg?component'

const { requireAuth } = useAuthGuard()
const router = useRouter()
const playerStore = usePlayerStore()
const shareModalStore = useShareModalStore()
const musicPreviewStore = useMusicPreviewStore()
const rightSongsDetailStore = useRightSongsDetailStore()
const mySongsStore = useMySongsStore()

// 从store获取状态
const currentSong = computed(() => playerStore.currentSong)
const isPlaying = computed(() => playerStore.isPlaying)
const currentTime = computed(() => playerStore.currentTime)
const duration = computed(() => playerStore.duration)
const volume = computed(() => playerStore.volume)
const playMode = computed(() => playerStore.playMode)
const songsList = computed(() => playerStore.songsList)
const currentSongIndex = computed(() => playerStore.currentSongIndex)

const isPerfDebugEnabled = () => {
  if (typeof window === 'undefined') return false
  try {
    if (window.localStorage?.getItem('perf_debug') === '1') return true
  } catch {}
  return !!(process as any).dev
}

const perfOn = isPerfDebugEnabled()

// 底部播放器使用的更多操作排除列表（与首页右侧详情保持一致）
const playerSongActionsExcludeIds = [
  'remix',
  'download',
  'edit_details',
  'publish',
  'move_to_trash'
]

const likeLoading = ref(false)
// 播放列表显示/隐藏状态
const showPlaylist = ref(false)

// 判断是否为第一首或最后一首歌曲
const isFirstSong = computed(() => {
  return currentSongIndex.value <= 0
})

const isLastSong = computed(() => {
  return currentSongIndex.value >= songsList.value.length - 1
})

 const shouldOpenRightSongsDetail = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth >= 768
 }

// 根据当前路由同步右侧预览/详情面板
const syncSidePanelsWithCurrentSong = (song: any) => {
  if (!song) return

  const path = router.currentRoute.value.path

  if (path.startsWith('/generate')) {
    // /generate 路由下，同步右侧 MusicPreviewSection
    musicPreviewStore.setPreviewSong(song)
  } else if (path === '/' || path.startsWith('/library')) {
    // /home 或 /library 路由下，同步右侧 RightSongsDetail
    if (shouldOpenRightSongsDetail()) {
      rightSongsDetailStore.openRightSongsDetail(song)
    }
  }
}

const markCurrentSongAsNotNewIfNeeded = (song: any) => {
  if (!song?.is_new || !song.uuid) return

  const path = router.currentRoute.value.path
  if (!(path.startsWith('/generate') || path.startsWith('/library'))) return

  const uuid = song.uuid

  const current = playerStore.currentSong as any
  if (current && current.uuid === uuid) {
    playerStore.currentSong = {
      ...current,
      is_new: 0 as any
    } as any
  }

  const list = songsList.value as any[]
  if (Array.isArray(list) && list.length > 0) {
    const nextList = list.map((item: any) =>
      item?.uuid === uuid
        ? {
            ...item,
            is_new: 0 as any
          }
        : item
    )
    playerStore.songsList = nextList as any
  }

  mySongsStore.updateSong(uuid, { is_new: 0 as any })

  setIsNotNew({ uuid: String(uuid) }).catch((error) => {
    console.warn('Failed to set song as not new', error)
  })
}

// 播放控制方法
const togglePlay = async () => {
  if (!currentSong.value) return

  if (isPlaying.value) {
    playerStore.pause()
  } else {
    await playerStore.playSong(currentSong.value)
  }
}

const playNext = async () => {
  await playerStore.playNext()
  if (playerStore.currentSong) {
    markCurrentSongAsNotNewIfNeeded(playerStore.currentSong as any)
  }
}

const playPrevious = async () => {
  await playerStore.playPrevious()
  if (playerStore.currentSong) {
    markCurrentSongAsNotNewIfNeeded(playerStore.currentSong as any)
  }
}

// 监听当前播放歌曲变化（包括自动切到下一首和手动上一/下一首），同步右侧预览/详情
watch(currentSong, (newSong) => {
  if (!newSong) return
  if (perfOn) {
    console.log('[perf][MusicPlayer] currentSong changed:', (newSong as any)?.uuid)
  }
  markCurrentSongAsNotNewIfNeeded(newSong)
  syncSidePanelsWithCurrentSong(newSong)
})

// 进度控制
const isSeeking = ref(false)
const seekingTime = ref(0)
const isDragging = ref(false)
const seekBarRef = ref<HTMLElement | null>(null)
const progressFillRef = ref<HTMLElement | null>(null)
const progressThumbWrapRef = ref<HTMLElement | null>(null)
const currentTimeTextRef = ref<HTMLElement | null>(null)
let seekPointerId: number | null = null
let seekRafId: number | null = null
let pendingSeekTime: number | null = null
let lastSeekCommittedTime: number | null = null
let seekBarRect: { left: number, width: number } | null = null
let iosVisualRafId: number | null = null
let iosVisualPendingTime: number | null = null
let iosVisualPendingClientX: number | null = null
let iosLastShownSecond: number | null = null

const isIOS = () => {
  if (typeof navigator === 'undefined') return false
  return /iP(hone|od|ad)/.test(navigator.userAgent)
}

const isWeChat = () => {
  if (typeof navigator === 'undefined') return false
  return /MicroMessenger/i.test(navigator.userAgent)
}

const isTouchLikeDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  const nav = navigator as Navigator & { msMaxTouchPoints?: number }
  const maxTouchPoints = Number(nav.maxTouchPoints || nav.msMaxTouchPoints || 0)
  if (Number.isFinite(maxTouchPoints) && maxTouchPoints > 0) return true
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
  } catch {
    return false
  }
}

let cachedLowPerfDragEnv: boolean | null = null
const isLowPerfDragEnv = () => {
  if (cachedLowPerfDragEnv !== null) return cachedLowPerfDragEnv
  cachedLowPerfDragEnv = isIOS() || isWeChat() || isTouchLikeDevice()
  return cachedLowPerfDragEnv
}

const clearSeekRaf = () => {
  if (typeof window === 'undefined') return
  if (seekRafId !== null) {
    cancelAnimationFrame(seekRafId)
    seekRafId = null
  }
  if (iosVisualRafId !== null) {
    cancelAnimationFrame(iosVisualRafId)
    iosVisualRafId = null
  }
  pendingSeekTime = null
  seekBarRect = null
  iosVisualPendingTime = null
  iosVisualPendingClientX = null
  iosLastShownSecond = null
}

const clearSeekGlobalListeners = () => {
  if (typeof window === 'undefined') return
  window.removeEventListener('blur', handleSeekWindowBlur)
}

const resetSeekState = (options?: { resetSeekingTime?: boolean }) => {
  isSeeking.value = false
  isDragging.value = false
  if (options?.resetSeekingTime) {
    seekingTime.value = 0
  }
  seekPointerId = null
  clearSeekRaf()
  clearSeekGlobalListeners()
  playerStore.resumeTimeUpdates()
}

const flushPendingSeek = (force = false) => {
  if (pendingSeekTime === null) return
  const next = pendingSeekTime
  if (!force && isDragging.value && isLowPerfDragEnv()) return

  playerStore.seek(next)
  lastSeekCommittedTime = next
  pendingSeekTime = null
}

const scheduleRealtimeSeek = (time: number) => {
  if (!safeDuration.value) return
  const next = Math.max(0, Math.min(time, safeDuration.value))
  seekingTime.value = next
  pendingSeekTime = next

  if (typeof window === 'undefined') {
    flushPendingSeek(true)
    return
  }

  if (isDragging.value && isLowPerfDragEnv()) return

  if (seekRafId !== null) return
  seekRafId = requestAnimationFrame(() => {
    seekRafId = null
    flushPendingSeek(false)
  })
}

const getSeekingTimeFromClientX = (clientX: number) => {
  if (!safeDuration.value) return 0

  const cached = seekBarRect
  const left = cached?.left ?? 0
  const width = cached?.width ?? 0
  if (!width) {
    const el = seekBarRef.value
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    seekBarRect = { left: rect.left, width: rect.width || 0 }
  }

  const nextCached = seekBarRect
  const finalLeft = nextCached?.left ?? left
  const finalWidth = nextCached?.width ?? width
  if (!finalWidth) return 0

  const raw = (clientX - finalLeft) / finalWidth
  const ratio = Number.isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0
  return ratio * safeDuration.value
}

const updateDragVisual = (time: number) => {
  const d = visualDuration.value
  if (!d) {
    if (isIndeterminateProgress.value) {
      if (progressFillRef.value) {
        progressFillRef.value.style.transform = ''
      }
      if (progressThumbWrapRef.value) {
        progressThumbWrapRef.value.style.transform = ''
      }
      return
    }

    if (progressFillRef.value) {
      progressFillRef.value.style.transform = 'scaleX(0)'
    }
    if (progressThumbWrapRef.value) {
      progressThumbWrapRef.value.style.transform = 'translate3d(0, 0, 0)'
    }
    return
  }

  let ratio = Math.max(0, Math.min(1, time / d))
  if (!safeDuration.value) {
    const song = currentSong.value as any
    const status = Number(song?.status)
    const progress = String(song?.progress || '')
    const isGenerating = status === 2 || progress === 'submitted' || progress === 'queued' || progress === 'streaming'
    if (isGenerating) {
      ratio = Math.min(ratio, 0.92)
    }
  }

  if (progressFillRef.value) {
    progressFillRef.value.style.transform = `scaleX(${ratio})`
  }

  if (progressThumbWrapRef.value) {
    progressThumbWrapRef.value.style.transform = `translate3d(${ratio * 100}%, 0, 0)`
  }

  if (currentTimeTextRef.value) {
    if (!isSeeking.value || !isLowPerfDragEnv()) return
    const sec = Math.floor(time)
    if (iosLastShownSecond !== sec) {
      iosLastShownSecond = sec
      currentTimeTextRef.value.textContent = formatTime(time)
    }
  }
}

const scheduleIOSVisualUpdate = (time: number) => {
  iosVisualPendingTime = time
  iosVisualPendingClientX = null
  if (typeof window === 'undefined') {
    updateDragVisual(time)
    return
  }
  if (iosVisualRafId !== null) return
  iosVisualRafId = requestAnimationFrame(() => {
    iosVisualRafId = null
    if (iosVisualPendingClientX !== null) {
      const next = getSeekingTimeFromClientX(iosVisualPendingClientX)
      pendingSeekTime = next
      updateDragVisual(next)
      return
    }
    if (iosVisualPendingTime === null) return
    updateDragVisual(iosVisualPendingTime)
  })
}

const scheduleLowPerfVisualUpdateByClientX = (clientX: number) => {
  iosVisualPendingClientX = clientX
  iosVisualPendingTime = null
  if (typeof window === 'undefined') return
  if (iosVisualRafId !== null) return
  iosVisualRafId = requestAnimationFrame(() => {
    iosVisualRafId = null
    if (iosVisualPendingClientX === null) return
    const next = getSeekingTimeFromClientX(iosVisualPendingClientX)
    pendingSeekTime = next
    updateDragVisual(next)
  })
}

const handleSeekWindowPointerMove = (event: PointerEvent) => {
  if (!isSeeking.value || !isDragging.value) return
  if (seekPointerId !== null && event.pointerId !== seekPointerId) return
  if (isLowPerfDragEnv()) {
    scheduleLowPerfVisualUpdateByClientX(event.clientX)
    return
  }

  scheduleRealtimeSeek(getSeekingTimeFromClientX(event.clientX))
}

const handleSeekBarPointerMove = (event: PointerEvent) => {
  if (isSeeking.value && isLowPerfDragEnv()) {
    event.preventDefault()
  }
  handleSeekWindowPointerMove(event)
}

const handleSeekWindowPointerUp = (event: PointerEvent) => {
  if (!isSeeking.value) return
  if (seekPointerId !== null && event.pointerId !== seekPointerId) return

  const time = getSeekingTimeFromClientX(event.clientX)

  if (isLowPerfDragEnv()) {
    pendingSeekTime = time
    updateDragVisual(time)
  } else {
    scheduleRealtimeSeek(time)
  }
  flushPendingSeek(true)
  resetSeekState()
}

const handleSeekBarPointerUp = (event: PointerEvent) => {
  if (isSeeking.value && isLowPerfDragEnv()) {
    event.preventDefault()
  }
  handleSeekWindowPointerUp(event)
}

const handleSeekBarPointerCancel = (event: PointerEvent) => {
  if (isSeeking.value && isLowPerfDragEnv()) {
    event.preventDefault()
  }
  handleSeekWindowPointerCancel(event)
}

const handleSeekWindowPointerCancel = (event: PointerEvent) => {
  if (!isSeeking.value) return
  if (seekPointerId !== null && event.pointerId !== seekPointerId) return

  flushPendingSeek(true)
  resetSeekState()
}

const handleSeekWindowBlur = () => {
  if (!isSeeking.value) return
  flushPendingSeek(true)
  resetSeekState()
}

const safeDuration = computed(() => {
  const d = Number(duration.value)
  return Number.isFinite(d) && d > 0 ? d : 0
})

const visualDuration = computed(() => {
  const d = safeDuration.value
  if (d) return d
  const song = currentSong.value as any
  const fallback = Number(song?.fileInfo?.duration || song?.metadata?.duration || 0)
  return Number.isFinite(fallback) && fallback > 0 ? fallback : 0
})

const isIndeterminateProgress = computed(() => {
  if (visualDuration.value) return false
  if (!isPlaying.value) return false
  const song = currentSong.value as any
  if (!song) return false
  const status = Number(song?.status)
  const progress = String(song?.progress || '')
  return status === 2 || progress === 'submitted' || progress === 'queued' || progress === 'streaming'
})

const isSeekAllowed = computed(() => {
  if (!safeDuration.value) return false
  const song = currentSong.value as any
  if (!song) return false
  const status = Number(song?.status)
  const progress = String(song?.progress || '')
  const isComplete = status === 3 || progress === 'complete'
  return isComplete
})

const displayedTime = computed(() => {
  return isSeeking.value ? seekingTime.value : currentTime.value
})

watch([displayedTime, visualDuration, isSeeking], ([time, d, seeking]) => {
  if (!d) {
    updateDragVisual(0)
    return
  }
  if (seeking && isLowPerfDragEnv()) return
  updateDragVisual(time)
}, { immediate: true })

const handleSeekPointerDown = (event: PointerEvent) => {
  if (!isSeekAllowed.value) return
  if (isSeeking.value) return

  event.preventDefault()
  event.stopPropagation()

  isSeeking.value = true
  isDragging.value = true
  seekPointerId = event.pointerId
  seekingTime.value = displayedTime.value
  lastSeekCommittedTime = displayedTime.value
  if (seekBarRef.value) {
    const rect = seekBarRef.value.getBoundingClientRect()
    seekBarRect = { left: rect.left, width: rect.width || 0 }
  }

  if (isLowPerfDragEnv()) {
    playerStore.suspendTimeUpdates()
  }

  if (isLowPerfDragEnv()) {
    scheduleLowPerfVisualUpdateByClientX(event.clientX)
  } else {
    scheduleRealtimeSeek(getSeekingTimeFromClientX(event.clientX))
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('blur', handleSeekWindowBlur)
  }

  try {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  } catch {
  }
}

watch(currentSong, () => {
  resetSeekState({ resetSeekingTime: true })
})

// 播放顺序控制
const playOrderIcon = computed(() => {
  return playMode.value === 'order' ? OrderOneByOneIcon : OrderSingleLoopIcon
})

const togglePlayOrder = () => {
  playerStore.togglePlayMode()
}

// 喜欢状态
const isLiked = computed(() => currentSong.value?.isVote === 1)

// 复用通用点赞逻辑，依赖全局 song-upvote-updated 事件来同步各处数据
const { handleToggleUpvote: handleToggleUpvoteFromPlayer } = useToggleUpvote<any>(async () => {
  // MusicPlayer 自身不需要额外本地更新，统一交给全局事件处理
})

const toggleLike = async () => {
  const authed = requireAuth()
  if (!authed || !currentSong.value || likeLoading.value) return

  likeLoading.value = true
  try {
    await handleToggleUpvoteFromPlayer(currentSong.value as any)
  } finally {
    likeLoading.value = false
  }
}

// 功能按钮方法（未实现具体功能）
const shareSong = () => {
  const authed = requireAuth()
  if (!authed || !currentSong.value || likeLoading.value) return
  const title = currentSong.value.metadata?.type === 'upload' && currentSong.value.title === 'my_2024-09-04_01_37_04' && currentSong.value.fullPrompt?.title || currentSong.value.title || currentSong.value.metadata?.prompt || `Untitled - ${formatDate(currentSong.value.createdAt)}`
  shareModalStore.openShare({
    title,
    uuid: currentSong.value.uuid
  })
}

const remixSong = () => {
  const authed = requireAuth()
  if (!authed || !currentSong.value) return

  const song = currentSong.value

  const payload: GeneratePrefillPayload = {
    action: 'generate_similar',
    song
  }

  const isOnGeneratePage = router.currentRoute.value.path === '/generate'

  const applyPrefill = async () => {
    if (typeof window !== 'undefined') {
      if (typeof (window as any).__setGenerateActiveTab === 'function') {
        ;(window as any).__setGenerateActiveTab('Custom')
      }
      await nextTick()
      window.dispatchEvent(new CustomEvent('generate:apply-prefill', { detail: payload }))
    }
  }

  if (isOnGeneratePage) {
    applyPrefill()
    return
  }

  if (typeof window !== 'undefined') {
    sessionStorage.setItem('generatePrefill', JSON.stringify(payload))
  }

  router.push('/generate').then(async () => {
    await nextTick()
    applyPrefill()
  })
}

const closePlaylist = () => {
  showPlaylist.value = false
}

const togglePlaylist = () => {
  showPlaylist.value = !showPlaylist.value
}

// 处理通过 SongActionsMenu 编辑歌曲详情成功后的回调：同步更新当前播放歌曲以及播放列表中的对应项
const handleSongMoreEditSuccess = (payload: { title: string, lyrics: string }) => {
  const current = playerStore.currentSong as any
  if (!current) return

  const updatedCurrent = {
    ...current,
    title: payload.title?.trim() || current.title,
    metadata: {
      ...current.metadata,
      prompt: payload.lyrics
    }
  }

  playerStore.currentSong = updatedCurrent as any

  const list = songsList.value as any[]
  if (Array.isArray(list) && list.length > 0) {
    const nextList = list.map((item: any) =>
      item?.uuid === updatedCurrent.uuid
        ? {
            ...item,
            title: updatedCurrent.title,
            metadata: {
              ...item.metadata,
              prompt: payload.lyrics
            }
          }
        : item
    )
    playerStore.songsList = nextList as any
  }
}

// 播放列表点击歌曲项
const playSongAtIndex = async (index: number, song: any) => {
  await playerStore.playSong(song, songsList.value)
}

// 获取歌曲时长
const getSongDuration = (song: any): number => {
  // 这里需要根据实际的歌曲数据结构来获取时长
  // 目前假设歌曲对象中没有直接的时长信息，返回0
  // console.log('song', song)
  const seconds = song.fileInfo?.duration || 0
  return seconds
}

// 音量控制
const showVolumePanel = ref(false)

// 根据音量大小显示不同的音量图标
const volumeIcon = computed(() => {
  // 目前只有一个音量图标，所以无论音量大小都返回同一个图标
  return VolumeIcon
})

const toggleVolumePanel = () => {
  showVolumePanel.value = !showVolumePanel.value
}

const closeVolumePanel = () => {
  showVolumePanel.value = false
}

const setVolume = (event: Event) => {
  const target = event.target as HTMLInputElement
  const vol = parseFloat(target.value)
  playerStore.setVolume(vol)
}

// 点击音量图标切换静音状态
const toggleMute = () => {
  playerStore.setVolume(volume.value > 0 ? 0 : 1)
}

const restoreVolumeFromMute = () => {
  playerStore.setVolume(0.5)
}

// 音量按钮点击事件：如果音量为0，则恢复音量，否则切换音量面板
const handleVolumeButtonClick = () => {
  if (volume.value === 0) {
    // 如果当前是静音状态，恢复之前的音量（默认50%）
    restoreVolumeFromMute()
  } else {
    // 否则切换音量面板显示
    toggleVolumePanel()
  }
}

// 封面图片
const getCoverImageUrl = (song?: any): string => {
  const targetSong = song || currentSong.value
  // 优先使用 fileInfo.cosUrl 作为封面
  if (targetSong?.fileInfo?.cosUrl) {
    return targetSong.fileInfo.cosUrl
  }
  // 如果没有 cosUrl，则使用默认的 picsum.photos 作为备用
  const seed = targetSong?.uuid ? targetSong.uuid.substring(0, 8) : 'default'
  return `https://picsum.photos/seed/${seed}/80/80`
}

// 格式化时间
const formatTime = (seconds: number): string => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// 进度条显示控制
const showProgress = ref(false)

// 标题滚动效果
const titleRef = ref<HTMLElement | null>(null)
const shouldScrollTitle = ref(false)
const isHoveringTitle = ref(false)
let scrollAnimation: number | null = null

const stopTitleScroll = () => {
  if (scrollAnimation) {
    cancelAnimationFrame(scrollAnimation)
    scrollAnimation = null
  }
  if (titleRef.value) {
    titleRef.value.style.transform = 'translateX(0)'
  }
}

// 检查标题是否需要滚动
const checkTitleOverflow = () => {
  if (!titleRef.value) return

  const parent = titleRef.value.parentElement
  if (!parent) return

  shouldScrollTitle.value = titleRef.value.scrollWidth > parent.clientWidth
}

const startTitleScroll = () => {
  if (isLowPerfDragEnv()) return
  if (!titleRef.value || !shouldScrollTitle.value || isHoveringTitle.value) return

  const parent = titleRef.value.parentElement
  if (!parent) return

  const scrollDistance = titleRef.value.scrollWidth - parent.clientWidth
  let start = 0
  const duration = 3000

  const animateScroll = (timestamp: number) => {
    if (!start) start = timestamp
    const elapsed = timestamp - start
    const progress = Math.min(elapsed / duration, 1)
    const translateX = -progress * scrollDistance

    if (titleRef.value) {
      titleRef.value.style.transform = `translateX(${translateX}px)`
    }

    if (progress < 1) {
      scrollAnimation = requestAnimationFrame(animateScroll)
    } else {
      setTimeout(() => {
        if (titleRef.value) {
          titleRef.value.style.transform = 'translateX(0)'
        }
        setTimeout(() => {
          startTitleScroll()
        }, 1000)
      }, 1000)
    }
  }

  scrollAnimation = requestAnimationFrame(animateScroll)
}

// 监听鼠标悬停
watch(isHoveringTitle, (newValue) => {
  if (newValue) {
    stopTitleScroll()
  } else {
    startTitleScroll()
  }
})

// 监听标题变化
watch(() => currentSong.value?.title, () => {
  stopTitleScroll()

  setTimeout(() => {
    checkTitleOverflow()
    startTitleScroll()
  }, 100)
})

// 监听窗口大小变化
const handleResize = () => {
  checkTitleOverflow()
}

const addGlobalListeners = () => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('song-upvote-updated', handleUpvoteUpdated as EventListener)
}

const removeGlobalListeners = () => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('song-upvote-updated', handleUpvoteUpdated as EventListener)
}

// 全局点赞状态更新事件监听：当 uuid 匹配当前歌曲或播放列表中的歌曲时，同步 isVote 和 upvote_count
const handleUpvoteUpdated = (event: Event) => {
  const customEvent = event as CustomEvent<{ uuid?: string, isVote?: number, upvote_count?: number }>
  const detail = customEvent.detail
  if (!detail?.uuid) return

  const { uuid, isVote, upvote_count } = detail

  // 更新当前歌曲
  const current = playerStore.currentSong as any
  if (current && current.uuid === uuid) {
    playerStore.currentSong = {
      ...current,
      isVote: typeof isVote === 'number' ? isVote : current.isVote,
      upvote_count: typeof upvote_count === 'number' ? upvote_count : current.upvote_count
    } as any
  }

  // 更新播放列表
  const list = songsList.value as any[]
  if (Array.isArray(list) && list.length > 0) {
    const nextList = list.map((item: any) =>
      item?.uuid === uuid
        ? {
            ...item,
            isVote: typeof isVote === 'number' ? isVote : item.isVote,
            upvote_count: typeof upvote_count === 'number' ? upvote_count : item.upvote_count
          }
        : item
    )
    playerStore.songsList = nextList as any
  }
}

onMounted(() => {
  const mountStart = perfOn ? performance.now() : 0
  checkTitleOverflow()
  startTitleScroll()
  addGlobalListeners()

  if (perfOn) {
    nextTick(() => {
      console.log('[perf][MusicPlayer] mounted -> nextTick ms:', (performance.now() - mountStart).toFixed(1))
    })
  }
})

onUnmounted(() => {
  stopTitleScroll()
  resetSeekState()
  removeGlobalListeners()
})

const formatDate = (value?: string | number | Date | null) => {
  if (!value) return ''

  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/)
    if (match && match[1]) return match[1]
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getUTCFullYear()
  const m = `${date.getUTCMonth() + 1}`.padStart(2, '0')
  const d = `${date.getUTCDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}
</script>

<template>
  <div
    class="music-player-container custom-skeleton relative"
    @mouseenter="showProgress = true"
    @mouseleave="showProgress = false"
  >
    <!-- 顶部进度条，鼠标悬停播放器时显示 -->
    <div
      ref="seekBarRef"
      class="progress-bar-wrapper absolute top-0 left-0 right-0 z-10"
      :class="isDragging ? 'h-[8px] md:h-[6px]' : 'h-[6px] md:h-[4px]'"
      @pointerdown="handleSeekPointerDown"
      @pointermove="handleSeekBarPointerMove"
      @pointerup="handleSeekBarPointerUp"
      @pointercancel="handleSeekBarPointerCancel"
    >
      <div
        class="progress-bar-top absolute top-0 left-0 right-0 transition-all duration-200 ease-out"
        :class="isDragging ? 'h-[8px] md:h-[6px]' : 'h-[6px] md:h-[4px]'"
      >
        <div class="progress-bar-track absolute inset-0 bg-gray-700 overflow-hidden rounded-full">
          <div
            class="progress-bar-fill h-full"
            ref="progressFillRef"
            :class="[
              isDragging ? 'progress-bar-fill--active' : 'bg-purple-500',
              isIndeterminateProgress
                ? 'progress-bar-fill--indeterminate transition-none'
                : (isSeeking ? 'transition-none' : 'transition-all duration-150 ease-linear')
            ]"
          />
        </div>

        <div
          v-if="visualDuration"
          class="progress-thumb-wrap pointer-events-none"
          ref="progressThumbWrapRef"
        >
          <div
            class="progress-thumb"
            :class="isDragging ? 'progress-thumb--dragging' : ''"
          />
        </div>
      </div>
    </div>

    <div class="flex items-center px-4 h-[64px] md:h-[68px] relative rounded-t-[12px] md:rounded-none bg-[#262636] md:bg-transparent">
      <!-- 移动端顶部紫色条 -->
      <!-- <div class="absolute left-0 right-0 top-0 h-[6px] bg-gradient-to-r from-[#D742FF] to-[#A742FF] md:hidden pointer-events-none" /> -->

      <!-- 左侧：封面、标题、作者、时间 -->
      <div
        class="song-info-section flex flex-1 items-center gap-3 min-w-0"
      >
        <!-- 封面 -->
        <div
          v-if="currentSong"
          class="song-cover size-12 flex-shrink-0"
        >
          <img
            v-if="(currentSong.status === 2 && currentSong.fileInfo?.cosUrl) || currentSong.status === 3"
            :src="currentSong?.fileInfo?.cosUrl"
            alt="Song Cover"
            class="size-full object-cover scale-[1.1] rounded-md"
          >
          <USkeleton
            v-else
            class="size-full rounded-[4px]"
          />
        </div>
        <div
          v-else
          class="w-12 h-12 flex-shrink-0"
        />

        <!-- 标题、作者、时间 -->
        <div
          v-if="currentSong"
          class="song-details flex flex-col justify-center overflow-hidden min-w-0"
        >
          <div class="song-title-container relative overflow-hidden">
            <h4
              ref="titleRef"
              class="song-title max-w-[120px] md:max-w-[500px] text-white text-sm font-medium whitespace-nowrap transition-transform duration-300"
              :class="{ 'animate-scroll': shouldScrollTitle && !isHoveringTitle && !isLowPerfDragEnv() }"
              @mouseenter="isHoveringTitle = true"
              @mouseleave="isHoveringTitle = false"
            >
              {{ currentSong.metadata?.type === 'upload' && currentSong.title === 'my_2024-09-04_01_37_04' && currentSong.fullPrompt?.title || currentSong.title || currentSong.metadata?.prompt || `Untitled - ${formatDate(currentSong.createdAt)}` }}
            </h4>
          </div>
          <div class="song-meta flex flex-col gap-1 text-xs text-gray-400">
            <span class="max-w-[120px] md:max-w-[400px] truncate">{{ currentSong.metadata?.tags || '(no styles)' }}</span>
            <span class="song-duration flex items-center gap-1">
              <span ref="currentTimeTextRef">{{ formatTime(displayedTime) }}</span>
              <span>/</span>
              <span v-if="Number.isFinite(duration) && duration > 0">
                {{ formatTime(duration) }}
              </span>
              <Loading
                v-else
                class="size-[12px] text-[#E7E7EC80] md:text-[#E7E7EC] animate-spin"
              />
            </span>
          </div>
        </div>
      </div>

      <!-- 中间：播放控制 -->
      <div
        class="flex items-center justify-center mx-3 md:mx-4 flex-none md:absolute md:left-1/2 md:-translate-x-1/2 z-10"
      >
        <!-- 播放控制按钮 -->
        <div class="flex items-center justify-center gap-3">
          <!-- 播放顺序（仅桌面） -->
          <button
            class="size-8 hidden md:flex items-center justify-center transition-colors"
            :class="{
              'text-white hover:text-purple-400': currentSong,
              'text-gray-500 cursor-not-allowed': !currentSong
            }"
            title="Play Order"
            :disabled="!currentSong"
            @click="togglePlayOrder"
          >
            <component
              :is="playOrderIcon"
              class="size-5"
            />
          </button>

          <!-- 上一曲 -->
          <button
            class="size-8 flex items-center justify-center transition-colors"
            :class="{
              'text-white hover:text-purple-400': currentSong && !isFirstSong,
              'text-gray-500 cursor-not-allowed': !currentSong || isFirstSong
            }"
            title="Previous"
            :disabled="!currentSong || isFirstSong"
            @click="playPrevious"
          >
            <PreIcon class="size-8" />
          </button>

          <!-- 播放/暂停 -->
          <button
            class="play-btn w-10 h-10 flex items-center justify-center bg-transparent border-2 border-white rounded-full transition-colors"
            :class="{
              'text-white hover:bg-white/10': currentSong,
              'text-gray-500 cursor-not-allowed': !currentSong
            }"
            title="Play/Pause"
            :disabled="!currentSong"
            @click="togglePlay"
          >
            <!-- <component
                :is="isPlaying ? PauseIcon : PlayIcon"
                class="w-6 h-6 ml-0.5"
              /> -->
            <UIcon
              v-if="!isPlaying"
              name="weui:play-filled"
              :class="currentSong ? 'size-[28px] text-[#E7E7EC]' : 'size-[28px] text-gray-500'"
            />
            <UIcon
              v-else
              name="weui:pause-filled"
              :class="currentSong ? 'size-[28px] text-[#E7E7EC]' : 'size-[28px] text-gray-500'"
            />
          </button>

          <!-- 下一曲 -->
          <button
            class="size-8 flex items-center justify-center transition-colors"
            :class="{
              'text-white hover:text-purple-400': currentSong && !isLastSong,
              'text-gray-500 cursor-not-allowed': !currentSong || isLastSong
            }"
            title="Next"
            :disabled="!currentSong || isLastSong"
            @click="playNext"
          >
            <NextIcon class="size-8" />
          </button>

          <!-- 播放列表（仅桌面） -->
          <button
            class="size-8 hidden md:flex items-center justify-center transition-colors"
            :class="{
              'text-white hover:text-purple-400': currentSong,
              'text-gray-500 cursor-not-allowed': !currentSong
            }"
            title="Playlist"
            :disabled="!currentSong"
            @click="togglePlaylist"
          >
            <ListIcon class="size-5" />
          </button>
        </div>
      </div>

      <!-- 右侧：功能按钮 -->
      <div
        class="volume-section flex flex-none items-center justify-end gap-3 ml-auto"
      >
        <!-- 喜欢 -->
        <button
          class="function-btn size-8 flex items-center justify-center transition-colors"
          :class="{
            'text-white hover:text-purple-400': currentSong,
            'text-gray-500 cursor-not-allowed': !currentSong
          }"
          title="Like"
          :disabled="!currentSong"
          @click="toggleLike"
        >
          <component
            :is="isLiked ? LikeActiveIcon : LikeIcon"
            class="size-8 md:size-5"
            :class="{ 'text-[#F24B4B]': isLiked, 'text-gray-500': !currentSong }"
          />
        </button>

        <!-- 更多（仅移动端）：与右侧详情使用相同的 SongActionsMenu -->
        <div
          v-if="currentSong"
          class="md:hidden"
          @click="requireAuth(() => {})"
        >
          <SongActionsMenu
            :song="currentSong as any"
            :exclude-ids="playerSongActionsExcludeIds"
            @edit-success="handleSongMoreEditSuccess"
          >
            <button
              class="function-btn size-8 flex items-center justify-center transition-colors"
              type="button"
            >
              <UIcon
                name="mdi:dots-horizontal"
                class="size-6 md:size-5 text-white"
              />
            </button>
          </SongActionsMenu>
        </div>

        <!-- 分享（仅桌面） -->
        <button
          class="function-btn size-8 hidden md:flex items-center justify-center transition-colors"
          :class="{
            'text-white hover:text-purple-400': currentSong,
            'text-gray-500 cursor-not-allowed': !currentSong
          }"
          title="Share"
          :disabled="!currentSong"
          @click="shareSong"
        >
          <ShareIcon
            class="size-5"
            :class="{ 'text-gray-500': !currentSong }"
          />
        </button>

        <!-- 二次创作（仅桌面） -->
        <button
          class="function-btn size-8 hidden md:flex items-center justify-center transition-colors"
          :class="{
            'text-white hover:text-purple-400': currentSong,
            'text-gray-500 cursor-not-allowed': !currentSong
          }"
          title="Remix"
          :disabled="!currentSong"
          @click="remixSong"
        >
          <RemakeIcon
            class="size-5"
            :class="{ 'text-gray-500': !currentSong }"
          />
        </button>

        <!-- 音量控制（仅桌面） -->
        <div class="relative hidden md:block">
          <button
            class="size-8 flex items-center justify-center transition-colors"
            :class="{
              'text-white hover:text-purple-400': currentSong,
              'text-gray-500 cursor-not-allowed': !currentSong
            }"
            title="Mute/Unmute"
            :disabled="!currentSong"
            @click="handleVolumeButtonClick"
            @dblclick="toggleMute"
          >
            <component
              :is="volumeIcon"
              class="size-5"
              :class="{ 'text-gray-500': !currentSong }"
            />
          </button>

          <!-- 音量调节面板，点击按钮后显示 -->
          <div
            v-if="showVolumePanel"
            class="volume-panel absolute bottom-full right-0 mb-2 bg-gray-900 bg-opacity-95 rounded-lg p-3 shadow-xl w-20 z-50"
            @click.stop
          >
            <div class="volume-panel-header flex items-center justify-center mb-3">
              <!-- <span class="volume-panel-title text-xs text-white font-medium">音量</span> -->
              <div class="volume-value text-xs text-gray-400">
                {{ Math.round(volume * 100) }}%
              </div>
            </div>
            <div class="volume-slider-container flex flex-col items-center gap-2">
              <!-- 音量滑块，垂直方向 -->
              <div class="volume-slider-wrapper flex items-center justify-center h-32">
                <input
                  type="range"
                  class="volume-slider appearance-none w-2 h-32 bg-gray-700 rounded-full cursor-pointer"
                  min="0"
                  max="1"
                  step="0.01"
                  :value="volume"
                  orient="vertical"
                  :style="{
                    '--volume-percentage': `${volume * 100}%`
                  }"
                  @input="setVolume"
                >
              </div>
              <!-- 音量图标在滑块下方 -->
              <component
                :is="volumeIcon"
                class="w-6 h-6 text-white"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 点击外部关闭音量面板 -->
    <div
      v-if="showVolumePanel"
      class="volume-panel-backdrop fixed inset-0 z-40"
      @click="closeVolumePanel"
    />

    <!-- 播放列表面板 -->
    <div
      v-if="showPlaylist"
      class="playlist-panel fixed bottom-[64px] right-4 bg-gray-900 bg-opacity-95 shadow-2xl z-50 w-[400px] h-[500px] overflow-y-auto rounded-lg"
    >
      <!-- 播放列表标题栏 -->
      <div class="playlist-header flex items-center justify-between px-4 py-3 border-b border-gray-700">
        <h3 class="playlist-title text-white font-medium text-lg">
          Playlist
        </h3>
        <div class="playlist-controls flex items-center gap-2">
          <span class="playlist-count text-gray-400 text-sm">{{ songsList.length }} songs</span>
          <button
            class="close-btn text-gray-400 hover:text-white transition-colors"
            title="Close"
            @click="closePlaylist"
          >
            <UIcon
              name="lucide:x"
              class="size-5"
            />
          </button>
        </div>
      </div>

      <!-- 播放列表内容 -->
      <div class="playlist-content">
        <ul class="playlist-items">
          <li
            v-for="(song, index) in songsList"
            :key="song.uuid"
            class="playlist-item flex items-center justify-between px-4 py-3 cursor-pointer transition-colors"
            :class="{
              'bg-purple-500 bg-opacity-20': index === currentSongIndex,
              'hover:bg-gray-800': index !== currentSongIndex
            }"
            @click="playSongAtIndex(index, song)"
          >
            <!-- 歌曲信息 -->
            <div class="song-info flex items-center gap-3 min-w-0">
              <!-- 播放状态图标 -->
              <div class="play-status w-6 h-6 flex items-center justify-center text-white">
                <UIcon
                  v-if="index === currentSongIndex && isPlaying"
                  name="weui:pause-filled"
                  class="size-4"
                />
                <UIcon
                  v-else-if="index === currentSongIndex"
                  name="weui:play-filled"
                  class="size-4"
                />
                <div
                  v-else
                  class="w-4 h-4"
                />
              </div>

              <!-- 歌曲封面 -->
              <div class="song-cover w-10 h-10 flex-shrink-0">
                <img
                  :src="getCoverImageUrl(song)"
                  alt="Song Cover"
                  class="w-full h-full object-cover rounded"
                >
              </div>

              <!-- 歌曲标题和艺术家 -->
              <div class="song-details flex flex-col min-w-0">
                <h4 class="song-title text-white text-sm font-medium truncate">
                  {{ song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' && song.fullPrompt?.title || song.title || song.metadata?.prompt || `Untitled - ${formatDate(song.createdAt)}` }}
                </h4>
                <span class="text-gray-400 text-xs truncate">{{ formatTime(getSongDuration(song)) }}</span>
              </div>
            </div>

            <!-- 歌曲时长 -->
            <!-- <div class="song-duration text-gray-400 text-xs ml-4">
              {{ formatTime(getSongDuration(song)) }}
            </div> -->
          </li>
        </ul>

        <!-- 空播放列表提示 -->
        <div
          v-if="songsList.length === 0"
          class="empty-playlist flex items-center justify-center py-12 text-gray-500 text-sm"
        >
          Playlist is empty
        </div>
      </div>
    </div>

    <!-- 点击外部关闭播放列表 -->
    <div
      v-if="showPlaylist"
      class="playlist-backdrop fixed inset-0 z-40"
      @click="closePlaylist"
    />
  </div>
</template>

<style scoped>
.custom-skeleton {
  --ui-bg-elevated: #383847;
}

.music-player-container {
    position: fixed;
    bottom: 0px;
    left: 0;
    right: 0;
    background: linear-gradient(90deg, #1a1a1a 0%, #2d2d2d 100%);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
    z-index: 1;
    margin-left: 200px;
    transition: all 300ms ease-in-out;
    isolation: isolate;
    transform: translateZ(0);
    will-change: transform;
}

@media (max-width: 768px) {
  .music-player-container {
    position: fixed;
    bottom: 60px;
    left: 0;
    right: 0;
    background: transparent;
    box-shadow: none;
    transition: none;
    z-index: 50;
    margin-left: 0px;
    transform: translateZ(0);
    will-change: transform;
  }
}

@media (min-width: 768px) {
  /* Adjust position for folded sidebar */
  body:has(.navigation-menu-container.w-\[60px\]) .music-player-container {
    width: calc(100% - 60px);
    margin-left: 60px;
  }
}

/* 标题滚动动画 */
@keyframes scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-100% + 100px));
  }
}

.animate-scroll {
  animation: scroll 8s linear infinite;
}

.animate-scroll:hover {
  animation-play-state: paused;
}

/* 自定义滑块样式 - 垂直音量滑块 */
.volume-slider {
  -webkit-appearance: none;
  appearance: none;
  background: #4B5563;
  background-size: 100% 100%;
  background-position: center;
  transition: background 0.1s ease;
  writing-mode: bt-lr; /* IE */
  -webkit-appearance: slider-vertical; /* Chrome, Safari, Opera */
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #1a1a2e;
  border: 2px solid #4B5563;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.volume-slider::-webkit-slider-thumb::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
}

.volume-slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.volume-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #1a1a2e;
  border: 2px solid #4B5563;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.volume-slider::-moz-range-thumb::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
}

.volume-slider::-moz-range-thumb:hover {
  transform: scale(1.1);
}

/* 进度条滑块样式 */
.progress-bar-wrapper {
  touch-action: none;
  user-select: none;
}

.progress-bar-input {
  -webkit-appearance: none;
  appearance: none;
  touch-action: none;
  user-select: none;
}

.progress-bar-fill--active {
  background: linear-gradient(90deg, #d742ff 0%, #a742ff 100%);
}

@keyframes progress-indeterminate {
  0% {
    transform: translate3d(-100%, 0, 0);
  }
  100% {
    transform: translate3d(250%, 0, 0);
  }
}

.progress-bar-fill--indeterminate {
  width: 40%;
  background: linear-gradient(90deg, rgba(215, 66, 255, 0.2) 0%, rgba(215, 66, 255, 1) 50%, rgba(215, 66, 255, 0.2) 100%);
  animation: progress-indeterminate 1.1s ease-in-out infinite;
}

.progress-bar-top {
  contain: layout;
  overflow: visible;
}

.progress-bar-track {
  contain: paint;
}

.progress-bar-wrapper {
  overflow: visible;
}

.progress-bar-fill {
  transform-origin: 0 50%;
  will-change: transform;
}

.progress-thumb-wrap {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: transform;
}

.progress-thumb {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%) scale(1);
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(215, 66, 255, 0.5);
  transition: transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease;
  opacity: 0.85;
}

.progress-thumb--dragging {
  transform: translate(-50%, -50%) scale(1.4);
  box-shadow: 0 0 0 5px rgba(215, 66, 255, 0.55);
  opacity: 1;
}

@media (pointer: coarse) {
  .progress-thumb {
    box-shadow: none;
  }

  .progress-thumb--dragging {
    box-shadow: none;
  }
}

.progress-bar-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}

.progress-bar-input::-moz-range-thumb {
  width: 12px;
  height: 12px;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

/* 音量面板样式 */
@media (min-width: 768px) {
  .volume-panel {
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
}
</style>
